import { Matrix, Vector3 } from '@babylonjs/core';
import { Dispatch, PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";

import { ElementLayoutResult, VCamera, VCoord, 
         Viewer3DContent } from '../3d-viewer/viewer-3d-content';
import { api2 } from './api-slice';
import { CatalogRoot, EMPTY_CATALOG_ROOT, Material } from './types/catalog-internal';
import { mainActions } from './main-slice';
import { calculateImplicitValues } from './properties';
import { RootState } from './store';
import { CfgCondition, CfgProperty, ElementAttachPointPosition, 
         InitialCamera, PropertyDefaultCase } from './types/catalog-types';
import { Composition, ElementInstance, ElementInstanceAdd, 
         MAIN_SCENE, OptionValues } from './types/composition';
import { EventEmitter, eventTypes, events } from './event-emitter';
import { showPopup } from './popup-slice';
import { generateImage } from '../3d-viewer/generate-image';
import { generatePDF, sendSalesParameters as sendSalesParametersAPI, 
         uploadImage, uploadImageAlt } from './api-client';
import { ResourceLoader } from '../3d-viewer/resource-loader';
import { calculateCountersUse, calculateInitialsAndDefaults, findGroupWithElement, 
         normalizeCounters, spreadCounterChange } from './configurator-helpers';

const IMAGE_WIDTH = 640;
const IMAGE_HEIGHT = 480;

export type ConfiguratorMode = 'compose' | 'options' | 'summary';

export interface QuestionValueType {
    tag?: string,
    material?: string,
    color?: string
};

export interface QuestionsObjectType {
    [clave: string]: QuestionValueType
}

export type DefaultsSet = Record<string, string|null|PropertyDefaultCase[]>;

const SPAWN_POSITIONS: [ number, number, number][] = [
    [  0, 0,  0 ],
    [  2, 0,  2 ],
    [ -2, 0,  2 ],
    [ -2, 0, -2 ],
    [  2, 0, -2 ]
]

export interface ConfiguratorState {

    // "location" of the configurator. What's being configured
    category: string
    family: string
    variant?: string

    // "catalog"
    catalog: CatalogRoot
    catalogReady: boolean   // this is used by the gatekeeper component, 
                            // so in the configurator use the catalog is ready

    isClassicModel: boolean
    showElementChooser: boolean
    addAttach?: {
        elementId: string
        position: ElementAttachPointPosition
        profile: string
    }
    selectedElement?: string
    cameraTarget?: VCoord
    cameraPosition?: VCoord

    mode: ConfiguratorMode
    currentQuestion: number
    currentStep: number
    currentNSteps: number
    questionData?: any
    scene: string

    initialValues: Record<string,OptionValues>
    // TODO: this seems not to be used
    defaults: DefaultsSet
    hasLoadedContent: boolean
    content: Composition
    animationPosition?: number

    justChanged: [string,string][]
    idGenerator: number
    currentSpawnPosition: number

    savingConfiguration: boolean
    pdfURL?: string
    glbURL?: string

    questionsValues: QuestionsObjectType
}

export type AddGroupData = {
    elements: ElementInstanceAdd[]
    position?: [ number, number, number ]
    showIf?: CfgCondition
    scene?: string
    initial: boolean
}

const initialState: ConfiguratorState = {

    category: '',
    family: '',

    catalog: EMPTY_CATALOG_ROOT,
    catalogReady: false,

    isClassicModel: false,
    showElementChooser: false,

    mode: 'options',
    currentQuestion: 0,
    currentStep: 0,
    currentNSteps: 1,
    scene: MAIN_SCENE,

    initialValues: {},
    defaults: {},
    hasLoadedContent: false,
    content: {
        groups: []
    },
    justChanged: [],
    idGenerator: 1001,
    currentSpawnPosition: 0,

    savingConfiguration: false,

    questionsValues: {},
};

// export const generatePDFAsync = createAsyncThunk<void, void, { state: RootState }>(
//     'cfg/generatePDF',
//     async (_, { getState, dispatch }) => {
//         const state = getState();
//         const sessionId = state.main.sessionId;
//         const language = state.main.lang;

//         if (sessionId) {
//             const pdfURL = await generatePDF(language, sessionId);
//             dispatch(configuratorActions.setPDFURL(pdfURL ?? undefined));
//         }
//     }
// );


export const saveConfigurationAsync = createAsyncThunk<void, { altMethod: boolean }, {state:RootState}>(
    'cfg/save',
    async ({ altMethod }, { getState, dispatch }) => {
        const state = getState();
        const { compose, initial } = state.configurator.catalog;
        const viewerContent = state.viewer.content;
        const snapshotCamera: InitialCamera | 'fit' | undefined = compose ? 'fit' : initial?.camera;
        const sessionId = state.main.sessionId;
        const language = state.main.lang;
        if(viewerContent && snapshotCamera && sessionId) {
            try {
                dispatch(configuratorActions.setSavingConfiguration(true));
                EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.EXPORT_GLB_EVENT);
                dispatch(showPopup({dialog: 'saveConfig'}))
                const image = await generateSaveImage(viewerContent, snapshotCamera, initial?.animationStart ?? 0);
                if(altMethod) {
                    await uploadImageAlt(image, sessionId);
                } else {
                    await uploadImage(image, sessionId);
                }
                const pdfURL = await generatePDF(language, sessionId);
                dispatch(configuratorActions.setPDFURL(pdfURL ?? undefined));

                const workCart = state.basket.workCartId;
                if(workCart) {
                    dispatch(api2.util.invalidateTags([
                        { type: 'Carts' },
                        { type: 'Cart', id: workCart }
                    ]))    
                }
            } finally {
                dispatch(configuratorActions.setSavingConfiguration(false));
            }

        }

    }
)

export const configuratorSlice = createSlice({
    name: 'cfg',
    initialState,
    reducers: {


        // ==== Initialization

        setLoadedContent(state, action: PayloadAction<Composition>) {
            state.hasLoadedContent = true;
            state.content = action.payload;
            state.currentSpawnPosition = action.payload.groups.length % SPAWN_POSITIONS.length;
            const groupsInMain = state.content.groups.filter(g => g.scene === MAIN_SCENE);
            state.selectedElement = groupsInMain[0].elements[0].id;
            state.idGenerator = state.content.groups.reduce(
                (groupAcc, groupCurrent) => {
                    return Math.max(
                        groupCurrent.elements.reduce(
                            (elementAcc, elementCurrent) => {
                                return Math.max(elementAcc, Number(elementCurrent.id))
                            },
                            Number(groupCurrent.id)
                        ),
                        Number(groupCurrent.id),
                        groupAcc
                    )
                },
                0
            ) + 1;
        },

        setConfiguratorPage(state, action: PayloadAction<{ category: string, family: string, variant?: string }>) {
            state.category = action.payload.category;
            state.family = action.payload.family;
            state.variant = action.payload.variant;
        },

        setCatalog: (state, action: PayloadAction<CatalogRoot>) => {
            state.catalog = action.payload;
            const [ values, defaults ] = calculateInitialsAndDefaults(action.payload.properties);
            state.initialValues = values;
            state.defaults = defaults;
            if(action.payload.compose) {
                configuratorSlice.caseReducers.activateCompose(state);
            } else {
                configuratorSlice.caseReducers.deactivateCompose(state,
                    configuratorActions.deactivateCompose({mode:'options'})
                )
            }
            state.catalogReady = true;
        },

        setIsClassicModel: (state, action: PayloadAction<boolean>) => {
            state.isClassicModel = action.payload;
        },


        // ==== Mode and navigation inside configurations

        activateCompose: (state) => {
            state.mode = 'compose';
            state.showElementChooser = true;
            state.currentQuestion = initialState.currentQuestion;
            state.currentStep = initialState.currentStep;
        },

        deactivateCompose: (state, action: PayloadAction<{ mode: ConfiguratorMode}>) => {
            state.mode = action.payload.mode;
            state.showElementChooser = initialState.showElementChooser;
            if(action.payload.mode !== 'options') {
                state.scene = MAIN_SCENE;
            }
        },

        setMode: (state, action: PayloadAction<ConfiguratorMode>) => {
            state.mode = action.payload;
            if(action.payload !== 'options') {
                state.scene = MAIN_SCENE;
            }
        },

        startAddElement: (state) => {
            state.showElementChooser = true;
            state.addAttach = undefined;
        },

        endAddElement: (state) => {
            state.showElementChooser = false;
            state.addAttach = undefined;
        },

        startAddAttach(state, action: PayloadAction<{ elementId: string, position: ElementAttachPointPosition, profile: string }>) {
            state.showElementChooser = true;
            state.addAttach = action.payload;
        },

        closeElementChooser: (state) => {
            state.showElementChooser = false;
            state.addAttach = undefined;
        },

        setSavingConfiguration: (state, action: PayloadAction<boolean>) => {
            state.savingConfiguration = action.payload;
            if(action.payload === true) {
                state.pdfURL = undefined;
            }
        },

        setPDFURL: (state, action: PayloadAction<string|undefined>) => {
            state.pdfURL = action.payload;
        },



        // ==== Questions and values

        setCurrentQuestion: (state, action: PayloadAction<{
            question: number, 
            nSteps: number, 
            step: number,
            scene?: string
        }>) => {
            const { question, nSteps, step: steps } = action.payload;
            state.currentQuestion = question;
            state.currentNSteps = nSteps;
            state.currentStep = steps;
            if(state.mode === 'options') {
                state.scene = action.payload.scene ?? MAIN_SCENE;
            }
        },

        setValues: (state, action: PayloadAction<{
            groupId?: string,
            values: OptionValues
        }>) => {

            const { groupId, values } = action.payload;
            const { properties } = state.catalog;
            state.justChanged = [];

            // If some properties are counters, the visible ones must be "spread"
            // through the groups. It's done before assigning because we need 
            // to know if the property increases.
            // We skip it if it is specific to a group ID (not Apply to All)
            // as this should always be Apply to All properties
            
            if(!groupId && state.content.groups.length > 0) {

                const implicitValuesByGroup = calculateImplicitValuesByGroup(state.content, state.defaults, state.catalog.qualities);
                const countersUse = calculateCountersUse(state.content, state.catalog, implicitValuesByGroup);
                const counterProperties = Object.keys(countersUse.maxShowablePerCounter);

                for(const [ counterName, value ] of Object.entries(values)) {

                    if(counterProperties.includes(counterName)) {

                        const groupWithProperty = state.content.groups.find(group => group.options[counterName] !== undefined);
                        if(!groupWithProperty) continue;
                        const referenceValues = groupWithProperty.options;                

                        const maxOfCounter = countersUse.maxShowablePerCounter[counterName];
                        const maxPerGroup = countersUse.maxShowablePerCounterGroup[counterName];
                        const refValue = Number.parseInt(referenceValues[counterName] ?? '0');
                        const newValue = Number.parseInt(value ?? '0');
                        let diff = newValue - refValue;
                        if(diff === 0) {
                            continue;
                        } else if(diff < 0 && newValue >= maxOfCounter) {
                            // there's still cushions not shown, do not remove anything
                            continue;
                        }
                        
                        // if there's a group selected, apply changes there
                        const selectedGroup = findGroupWithElement(state.content.groups, state.selectedElement);
                        if(selectedGroup) {
                            
                            if(maxPerGroup) {
                                const maxOfGroup = maxPerGroup[selectedGroup.id] ?? 0;
                                const amountOfGroup = (selectedGroup.counters ?? {})[counterName] ?? 0
                                
                                let newAmount = amountOfGroup + diff;
                                if(diff > 0) {
                                    if(newAmount > maxOfGroup) {
                                        diff = newAmount - maxOfGroup;
                                        newAmount = maxOfGroup;
                                    } else {
                                        diff = 0;
                                    }
                                } else {
                                    if(newAmount < 0) {
                                        diff = newAmount;
                                        newAmount = 0;
                                    } else {
                                        diff = 0;
                                    }
                                }
                                if(!selectedGroup.counters) {
                                    selectedGroup.counters = {};
                                }
                                selectedGroup.counters[counterName] = newAmount;
                            }
                            
                        } 

                        // apply pending changes
                        spreadCounterChange(state.content.groups, counterName, diff, maxPerGroup);

                    }

                }
            }


            // Assign the values to the content groups

            for(const group of state.content.groups) {
                if(groupId === group.id || groupId === undefined) {

                    const newValues = Object.assign({}, group.options);

                    for(const [key, value] of Object.entries(values)) {

                        // determine if must apply to this group
                        const property = properties.find(p => p.name === key);
                        let propertyScenes = [ MAIN_SCENE ];
                        if(property && property.scene) {
                            propertyScenes = Array.isArray(property.scene) ? property.scene : [ property.scene ];
                        }

                        if(propertyScenes.includes(group.scene)) {
                            if(value == null) {
                                delete newValues[key];
                            } else {
                                newValues[key] = value;
                            }
                            state.justChanged.push([group.id, key]);    
                        }                        
                    }
                    group.options = newValues;
                }    
            }
            
        },

        setQuestionData: (state, action: PayloadAction<any>) => {
            state.questionData = action.payload;
        },

        clearQuestionData: (state) => {
            state.questionData = undefined;
        },

        setQuestionsValues: (state, action: PayloadAction<{ stepId: keyof typeof state.questionsValues, stepValues: QuestionValueType  }>) => {
            const { stepId, stepValues } = action.payload;
            state.questionsValues[stepId] = {...state.questionsValues[stepId], ...stepValues}
        },




        // ==== interaction with 3D viewer

        setAnimationPosition: (state, action: PayloadAction<number>) => {
            state.animationPosition = action.payload;
        },

        setCamera(state, action: PayloadAction<{ target: VCoord, position: VCoord }>) {
            state.cameraTarget = action.payload.target;
            state.cameraPosition = action.payload.position;
        },

        setGlbURL: (state, action: PayloadAction<string|undefined>) => {
            state.glbURL = action.payload;
        },


        // ==== Selection

        selectElement(state, action: PayloadAction<string>) {
            state.selectedElement = action.payload;
        },

        selectGroup(state, action: PayloadAction<string>) {
            const currentGroup = state.content.groups.find(g => g.elements.findIndex(e => e.id === state.selectedElement) >= 0);
            if(!currentGroup || currentGroup.id !== action.payload) {
                const newGroup = state.content.groups.find(g => g.id === action.payload);
                state.selectedElement = newGroup?.elements[0].id;
            }
        },



        // ==== Composition: Groups

        setGroups: (state, action: PayloadAction<AddGroupData[]>) => {
            const groups = action.payload;
            state.content = { groups: [] };
            state.idGenerator = initialState.idGenerator;
            for(const group of groups) {
                configuratorSlice.caseReducers.addGroup(state, configuratorSlice.actions.addGroup(group));
            }
            normalizeCounters(state.content, state.catalog, state.defaults);
        },

        addGroup: (state, action: PayloadAction<AddGroupData>) => {
            const group = action.payload;
            const groupId = state.idGenerator++;
            let firstElementId: string|undefined;

            let options: OptionValues;
            if(state.selectedElement) {
                const existingGroup = state.content.groups.find(
                    g => g.elements.findIndex(
                        e => e.id === state.selectedElement && g.scene === (group.scene ?? MAIN_SCENE)
                    ) >= 0
                );
                if(existingGroup) {
                    options = { ...existingGroup.options };
                } else {
                    options = { ...state.initialValues[group.scene ?? MAIN_SCENE] };
                }
            } else {
                options = { ...state.initialValues[group.scene ?? MAIN_SCENE] };
            }

            let position: [ number, number, number];
            if(group.position) {
                position = group.position;            
            } else if(group.initial) {
                position = [0, 0, 0];
            } else {                
                // if it is empty, always start in the center
                if(state.content.groups.filter(g => g.scene === MAIN_SCENE && !g.initial).length === 0) {
                    position = [0, 0, 0];
                    state.currentSpawnPosition = 1;
                } else {
                    position = SPAWN_POSITIONS[state.currentSpawnPosition];
                    state.currentSpawnPosition = (state.currentSpawnPosition + 1) % SPAWN_POSITIONS.length;
                }
            }

            const newGroup = {
                id: groupId.toString(),
                options,
                elements: group.elements.map(e => {
                    const elementId = state.idGenerator++;
                    if(!firstElementId) {
                        firstElementId = elementId.toString();
                    }
                    return {
                        id: elementId.toString(),
                        element: e.element
                    }
                }),
                position,
                rotation: 0,
                showIf: group.showIf,
                scene: group.scene ?? MAIN_SCENE,
                initial: group.initial
            }

            state.content.groups.push(newGroup);
            if(!group.scene || group.scene === MAIN_SCENE) {
                state.selectedElement = firstElementId;
            }
            
            normalizeCounters(state.content, state.catalog, state.defaults);
        },

        moveGroup: (state, action: PayloadAction<{ groupId: string, position: VCoord }>) => {
            const group = state.content.groups.find(g => g.id === action.payload.groupId);
            if(group) {
                group.position = action.payload.position;
            }
        },

        _rotateGroup: (state, action: PayloadAction<{ group: string, actionElement: string, layout: Record<string, ElementLayoutResult>}>) => {
            const group = state.content.groups.find(g => g.id === action.payload.group);
            if(group) {
                const pivotElement = group.elements[0].id;
                if(pivotElement !== action.payload.actionElement) {
                    const pivotPosition = group.position;
                    const actionElementLayout = action.payload.layout[action.payload.actionElement];
                    const actionPosition = Vector3.FromArray(actionElementLayout.position);

                    const translation1 = Matrix.Translation(pivotPosition[0], pivotPosition[1], pivotPosition[2]);
                    const rotationMatrix = Matrix.RotationY(Math.PI / 2);
                    const translation2 = Matrix.Translation(-pivotPosition[0], -pivotPosition[1], -pivotPosition[2]);
                    const transform = translation2.multiply(rotationMatrix).multiply(translation1);

                    const newActionPosition = Vector3.TransformCoordinates(actionPosition, transform);
                    const diff = actionPosition.subtract(newActionPosition);
                    group.position = [
                        group.position[0] + diff.x, 
                        group.position[1] + diff.y, 
                        group.position[2] + diff.z
                    ];
                }

                group.rotation = ((group.rotation ?? 0) + 90 % 360);
            }
        },

        deleteGroup: (state, action: PayloadAction<string>) => {
            const isSelectedElementGroup = state.content.groups.find(g => g.elements.findIndex(e => e.id === state.selectedElement) >= 0)?.id === action.payload;
            state.content.groups = state.content.groups.filter(g => g.id !== action.payload);
            if(isSelectedElementGroup) {
                const groupsInMain = state.content.groups.filter(g => g.scene === MAIN_SCENE);
                if(groupsInMain.length > 0) {
                    state.selectedElement = groupsInMain[0].elements[0].id;
                } else {
                    state.selectedElement = undefined;                
                }
            }


            
            normalizeCounters(state.content, state.catalog, state.defaults);
        },

        deleteAllGroups: (state) => {
            // Set all counters to 0 in each group and find all groups with counters
            var groups = state.content.groups
            //Find all counternames in all groups
            const differentCounters = new Set<string>(); // Set!!!! for unique items
            groups.forEach(group => { 
                if (group.counters) {
                    for (const counterName of Object.keys(group.counters)) {
                        if (group.counters[counterName] > 0) {
                            differentCounters.add(counterName);
                        }
                    }
                }
            });


            // Cushion-related options to reset
            const cushionInitials: Record<string, string> = {
                cushion: "no",
                cushionmodel: "cush",
                cushiontype: "none",
                cushionquantity: "1",
                cushmat: "lacquer/black", // alleen bij relaxen maar we kunnen het gewoon proberen resetten als het bestaat
                cushioncovering0: "*PROPERTY:covering0"
            };


            // Filter initial groups and not main scene groups & reset counters
            state.content.groups = groups
            .filter(g => g.scene !== MAIN_SCENE || g.initial === true)
            .map(group => {
                if (group.options) {
                    differentCounters.forEach(counterName => {
                        if (group.options.hasOwnProperty(counterName)) {
                            group.options[counterName] = "0";
                        }
                    });
                }

                // Reset cushion-related options
                Object.keys(cushionInitials).forEach(optionName => {
                    if (group.options.hasOwnProperty(optionName)) {
                        group.options[optionName] = cushionInitials[optionName];
                    }
                });

                // Convert all "yes" values to "no"
                Object.keys(group.options).forEach(optionKey => {
                    if (group.options[optionKey] === "yes") {
                        group.options[optionKey] = "no";
                    }
                });
            
                return group; 
            });

            console.log(state.content.groups)

        
            state.selectedElement = undefined;

            normalizeCounters(state.content, state.catalog, state.defaults);
        },



        // ==== Composition: Elements

        addAttachedElement: (state, action: PayloadAction<{
            existing: string, 
            newCatalogId: string, 
            oldPosition: ElementAttachPointPosition,
            newPosition: ElementAttachPointPosition,
            profile: string
        }>) => {
            const group = state.content.groups.find(g => g.elements.findIndex(e => e.id === action.payload.existing) >= 0);
            if(group) {
                const elementId = state.idGenerator++;
                group.elements.push({
                    id: elementId.toString(),
                    element: action.payload.newCatalogId,
                    attachedTo: {
                        otherId: action.payload.existing,
                        otherPosition: action.payload.oldPosition,
                        ownPosition: action.payload.newPosition
                    }
                });
                if(state.selectedElement) {
                    state.selectedElement = elementId.toString();
                }
            }
            normalizeCounters(state.content, state.catalog, state.defaults);
        },

        // the main (invokable) action is a thunk that retrieves the elements layout and passes it to this one
        _deleteElement: (state, action: PayloadAction<{element:string, layout: Record<string, ElementLayoutResult>}>) => {
            const sourceGroup = state.content.groups.find(g => g.elements.findIndex(e => e.id === action.payload.element) >= 0);
            if(sourceGroup) {

                const groups: ElementInstance[][] = [];

                if(sourceGroup.elements.length === 1) {
                    // lonely element, remove the group
                    state.content.groups = state.content.groups.filter(g => g.id !== sourceGroup.id);                    
                    if(state.selectedElement === action.payload.element) {
                        const groupsInMain = state.content.groups.filter(g => g.scene === MAIN_SCENE);
                        if(groupsInMain.length > 0) {
                            state.selectedElement = groupsInMain[0].elements[0].id;
                        } else {
                            state.selectedElement = undefined;
                        }
                    }    
                } else {
              
                    for(const e of sourceGroup.elements) {
                        if(e.id === action.payload.element) {
                            // nop, we are removing this
                        } else {
                            if(e.attachedTo) {
                                // it is attached to another element
                                const targetGroup = groups.find(g => g.findIndex(e2 => e2.id === e.attachedTo?.otherId) >= 0);
                                if(targetGroup) {
                                    targetGroup.push(e);
                                } else {
                                    delete e.attachedTo;
                                    groups.push([e]);
                                }
                            } else {
                                // it is the first element
                                groups.push([e]);
                            }
                        }
                    }
                    sourceGroup.elements = groups[0];
                    for(let i = 1; i < groups.length; i++) {
                        const newId = state.idGenerator++;
                        const firstElement = groups[i][0];
                        const elementLayoutResult = action.payload.layout[firstElement.id];
                        const newGroup = {
                            id: newId.toString(),
                            options: { ...sourceGroup.options },
                            elements: groups[i],
                            position: elementLayoutResult.position,
                            rotation: elementLayoutResult.rotation[1],
                            showIf: sourceGroup.showIf,
                            scene: sourceGroup.scene
                        }
                        state.content.groups.push(newGroup);
                    }

                    if(state.selectedElement === action.payload.element) {
                        state.selectedElement = groups[0][0].id;
                    }

                }

    
            }
            normalizeCounters(state.content, state.catalog, state.defaults);
        }

    },

    extraReducers: (builder) => {
        builder.addCase(mainActions.enterPage, (state,action) => {
            if(action.payload !== 'configurator') {
                resetState(state);
                state.category = '';
                state.family = '';
                state.variant = '';
            }
        })
    }
});

export const rotateGroup = (group: string, actionElement: string) => (dispatch: Dispatch, getState: () => RootState) => {
    const layout = getState().viewer.elementsLayout;
    dispatch(configuratorActions._rotateGroup({
        group, actionElement, layout
    }))
}

export const deleteElement = (element: string) => (dispatch: Dispatch, getState: () => RootState) => {
    const layout = getState().viewer.elementsLayout;
    dispatch(configuratorActions._deleteElement({
        element, layout
    }))
}

export const sendSalesParameters = () => (dispatch: Dispatch, getState: () => RootState) => {
    const mainState = getState().main;
    const salesInfo = mainState.salesInfo;
    const sessionid = mainState.sessionId;
    if(salesInfo && sessionid) {
        sendSalesParametersAPI(sessionid, salesInfo)
    }
    
}

export const configuratorActions = {
    ...configuratorSlice.actions,
    rotateGroup,
    deleteElement,
    sendSalesParameters,
    saveConfiguration: saveConfigurationAsync
};

export default configuratorSlice.reducer;



// ==== selectors ====
// TODO this should be migrated gradually to configurator-selectors
//      I don't do it all at once now, because I suspect there's data
//      set in state that really can be derived (ex. hasComposeSelector)
//      but at the moment of writing this I can't risk breaking it.

export const isClassicModelSelector = (state: RootState) => state.configurator.isClassicModel;
export const modeSelector = (state: RootState) => state.configurator.mode;
export const showElementChooserSelector = (state: RootState) => state.configurator.showElementChooser;
export const addAttachSelector = (state: RootState) => state.configurator.addAttach;
export const selectedElementSelector = (state: RootState) => state.configurator.selectedElement;
export const defaultsSelector = (state: RootState) => state.configurator.defaults;
export const animationPositionSelector = (state: RootState) => state.configurator.animationPosition;
export const justChangedSelector = (state: RootState) => state.configurator.justChanged;
export const contentSelector = (state: RootState) => state.configurator.content;
export const savingConfigurationSelector = (state: RootState) => state.configurator.savingConfiguration;
export const pdfURLSelector = (state: RootState) => state.configurator.pdfURL;
export const questionValuesSelector = (state: RootState) => state.configurator.questionsValues;
export const sceneSelector = (state: RootState) => state.configurator.scene;
export const hasLoadedContentSelector = (state: RootState) => state.configurator.hasLoadedContent;
export const glbURLSelector = (state: RootState) => state.configurator.glbURL;


export const selectedGroupSelector = createSelector(
    contentSelector,
    selectedElementSelector,
    (content, selectedElement) => {
        return findGroupWithElement(content.groups, selectedElement);
    }
);

export const selectedElementsSelector = createSelector(
    selectedGroupSelector,
    (selectedGroup) => {
        if(selectedGroup === undefined) {
            return [];
        } else {
            return selectedGroup.elements.map(e => e.element);
        }
        
    }
)


export const selectedValuesSelector = createSelector(
    selectedGroupSelector,
    (group) => {
        if(!group) {
            return {};
        } else {
            return group.options;
        }
    }
)

export const currentSceneValuesSelector = createSelector(
    sceneSelector,
    contentSelector,
    (scene, content) => {
        const group = content.groups.find(g => g.scene === scene);
        if(group) {
            return group.options;
        } else {
            return {};
        }    
    }
)

export const selectedImplicitValuesSelector = createSelector(
    defaultsSelector,
    selectedValuesSelector,
    (state: RootState) => state.configurator.catalog.qualities,
    (defaults, values, qualities) => {
        return calculateImplicitValues(defaults, values, qualities);
    }
);

export const implicitValuesByGroupSelector = createSelector(
    contentSelector,
    defaultsSelector,
    (state: RootState) => state.configurator.catalog.qualities,
    (content, defaults, qualities) => calculateImplicitValuesByGroup(content, defaults, qualities)
);

// ==== helpers ====

export function calculateImplicitValuesByGroup(content: Composition, defaults: DefaultsSet, qualities: Material[] = []) {
    return Object.fromEntries(content.groups.map(g => 
        [g.id, calculateImplicitValues(defaults, g.options, qualities)]
    ));
}

async function generateSaveImage(content: Viewer3DContent, snapshotCamera: InitialCamera | 'fit', animationPercentage: number): Promise<Blob> {
    // TODO: review this camera numbers, they don't make sense, but probably are imposible to happen also
    const camera: VCamera = snapshotCamera === 'fit' ? {
        position: [0,0,2],
        target: [0,1,0]
    } : {
        position: snapshotCamera.position ?? [0,0,2],
        target: snapshotCamera.target ?? [0,1,0]
    }
    const defaultCameraContent = {
        ...content,
        scenes: {
            ...content.scenes,
            main: {
                ...content.scenes.main,
                camera
            }
        }
    }    
    // @ts-expect-error
    const resourceLoader = window.RESOURCE_LOADER as ResourceLoader;
    // const resourceLoader = new ResourceLoader(import.meta.env.VITE_ASSETS);
    const image = await generateImage(defaultCameraContent, IMAGE_WIDTH, IMAGE_HEIGHT, snapshotCamera === 'fit', animationPercentage, resourceLoader);
    return image;
}

function resetState(state: ConfiguratorState) {
    state.catalog = initialState.catalog;
    state.catalogReady = initialState.catalogReady;
    state.hasLoadedContent = initialState.hasLoadedContent;
    state.currentQuestion = initialState.currentQuestion;
    state.currentStep = initialState.currentStep;
    state.currentNSteps = initialState.currentNSteps;
    state.initialValues = initialState.initialValues;
    state.defaults = initialState.defaults;
    state.content = initialState.content;
    state.justChanged = initialState.justChanged;
    state.idGenerator = initialState.idGenerator;
    state.questionsValues = initialState.questionsValues;
    state.isClassicModel = initialState.isClassicModel;
    state.showElementChooser = initialState.showElementChooser;
    state.addAttach = initialState.addAttach;
}